"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommandExecutionRow = exports.CommandStatus = exports.PlayerCommandAck = exports.PlayerCommandIssue = exports.CommandType = void 0;
// P5 W1 — Operator command surface (FS Phase 5 §2.2, §2.4).
//
// The four MVP commands ship in Wave 1.
// W4 appended player.upgrade_software (FS §4.3, signed bundle push).
// W3 appended player.reboot, player.standby, player.wake (FS §2.1).
// Append-only: never reorder or remove entries.
const zod_1 = require("zod");
exports.CommandType = zod_1.z.enum([
    'player.pause_toggle',
    'player.clear_cache',
    'player.set_volume',
    'player.capture_screenshot',
    // P5 W4 — signed software push (FS §4.3). Appended; do NOT reorder.
    'player.upgrade_software',
    // P5 W3 — Class-A power commands (FS §2.1). Appended; do NOT reorder.
    'player.reboot',
    'player.standby',
    'player.wake',
]);
/**
 * Per-command args. Discriminated on `command_type` so the
 * zod-narrowed branch carries a precise args shape. The full
 * envelope (the row that lives in the CommandExecution table)
 * carries `command_id`, `tenant_id`, `player_id`, `issued_by_id`,
 * `issued_at`, `not_after`; the schema below is the WIRE input that
 * the admin SPA POSTs to /api/v1/admin/players/:id/commands — the
 * server fills in everything else.
 */
exports.PlayerCommandIssue = zod_1.z.discriminatedUnion('command_type', [
    zod_1.z.object({ command_type: zod_1.z.literal('player.pause_toggle'), args: zod_1.z.object({}) }),
    zod_1.z.object({
        command_type: zod_1.z.literal('player.clear_cache'),
        args: zod_1.z.object({ preserve_pairing: zod_1.z.boolean().default(true) }),
    }),
    zod_1.z.object({
        command_type: zod_1.z.literal('player.set_volume'),
        args: zod_1.z.object({ level: zod_1.z.number().min(0).max(1) }),
    }),
    zod_1.z.object({
        command_type: zod_1.z.literal('player.capture_screenshot'),
        args: zod_1.z.object({
            max_dimension: zod_1.z.number().int().min(64).max(3840).default(1920),
            format: zod_1.z.enum(['jpeg', 'png']).default('jpeg'),
            max_bytes: zod_1.z
                .number()
                .int()
                .min(1024)
                .max(8 * 1024 * 1024)
                .default(2 * 1024 * 1024),
        }),
    }),
    // P5 W4 — signed software/firmware push (FS §4.3). Appended last;
    // do NOT reorder or remove. Args mirror PlayerUpgradeAvailable so
    // the player can verify the manifest signature before applying.
    zod_1.z.object({
        command_type: zod_1.z.literal('player.upgrade_software'),
        args: zod_1.z.object({
            bundle_id: zod_1.z.string().uuid(),
            version: zod_1.z.string(),
            bytes_url: zod_1.z.string(),
            bytes_sha256: zod_1.z.string().regex(/^[0-9a-f]{64}$/),
            byte_size: zod_1.z.number().int().positive(),
            manifest_canonical_json: zod_1.z.string().min(1),
            signature_b64: zod_1.z.string().min(1),
            signing_key_id: zod_1.z.string().uuid(),
        }),
    }),
    // P5 W3 — Class-A power commands (FS §2.1). Appended; do NOT reorder.
    zod_1.z.object({
        command_type: zod_1.z.literal('player.reboot'),
        args: zod_1.z.object({
            grace_seconds: zod_1.z.number().int().nonnegative().max(60).default(5),
        }),
    }),
    zod_1.z.object({
        command_type: zod_1.z.literal('player.standby'),
        args: zod_1.z.object({}),
    }),
    zod_1.z.object({
        command_type: zod_1.z.literal('player.wake'),
        args: zod_1.z.object({}),
    }),
]);
/**
 * ACK emitted by the player over the playback.event WS channel.
 * Per FS §2.3 — the same channel as playback events so the gateway
 * doesn't need a third subscription. `status='ok'` is the success
 * path; everything else is a structured failure consumers should
 * surface in the admin UI.
 */
exports.PlayerCommandAck = zod_1.z.object({
    type: zod_1.z.literal('command.completed'),
    command_id: zod_1.z.string().uuid(),
    status: zod_1.z.enum(['ok', 'failed', 'unsupported', 'stale', 'rejected']),
    started_at: zod_1.z.string().datetime(),
    completed_at: zod_1.z.string().datetime(),
    result: zod_1.z.record(zod_1.z.unknown()).nullable(),
    error: zod_1.z
        .object({
        code: zod_1.z.string(),
        message: zod_1.z.string(),
    })
        .nullable(),
});
/**
 * Admin-namespace WS push. Mirrors PlayerCommandAck but adds
 * `player_id` so an admin watching a multi-player view can route
 * the toast to the right card without an extra lookup.
 */
exports.CommandStatus = zod_1.z.object({
    type: zod_1.z.literal('command.status'),
    command_id: zod_1.z.string().uuid(),
    player_id: zod_1.z.string().uuid(),
    status: zod_1.z.enum([
        'issued',
        'ok',
        'failed',
        'unsupported',
        'stale',
        'rejected',
        'completed',
    ]),
    at: zod_1.z.string().datetime(),
    detail: zod_1.z.record(zod_1.z.unknown()).optional(),
});
/**
 * Full row shape (server-only — never sent to player). Used by
 * the admin /admin/players/:id detail page to render the
 * command history list.
 */
exports.CommandExecutionRow = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    tenant_id: zod_1.z.string().uuid(),
    player_id: zod_1.z.string().uuid(),
    command_type: exports.CommandType,
    args: zod_1.z.record(zod_1.z.unknown()),
    issued_by_id: zod_1.z.string().uuid(),
    issued_at: zod_1.z.string().datetime(),
    not_after: zod_1.z.string().datetime(),
    status: zod_1.z.enum([
        'issued',
        'acked',
        'completed',
        'failed',
        'unsupported',
        'stale',
        'rejected',
        'expired',
    ]),
    ack_at: zod_1.z.string().datetime().nullable(),
    completed_at: zod_1.z.string().datetime().nullable(),
    result: zod_1.z.record(zod_1.z.unknown()).nullable(),
    error: zod_1.z.object({ code: zod_1.z.string(), message: zod_1.z.string() }).nullable(),
});
//# sourceMappingURL=command.schema.js.map